import type {Headers} from '@sail/data';

import version from '../../schema/version';

import type {V1ContextInput} from '../../schema/types';

export const STRIPE_CONTEXT_HEADER_FOR_V1 =
  'X-Client-Only-Stripe-Context-For-V1-Queries';
export const STRIPE_VERSION_HEADER = 'Stripe-Version';

export const ACCOUNT_HEADER = 'Stripe-Account';
const PATHED_ACCOUNT_HEADER = 'X-Client-Only-Pathed-Stripe-Account';
const AUTHORIZATION_HEADER = 'Authorization';
const CONTENT_TYPE_HEADER = 'Content-Type';
const LIVEMODE_HEADER = 'Stripe-Livemode';
const REQUESTED_WITH_HEADER = 'X-Requested-With';

function getLiveMode(
  value?: V1ContextInput['liveMode'],
): 'true' | 'false' | null {
  switch (value) {
    case 'FORCE_LIVE':
      return 'true';
    case 'FORCE_TEST':
      return 'false';
    default:
      return null;
  }
}

export default function addContextHeaders(
  headers: Headers,
  v1Context: V1ContextInput,
) {
  const {
    accountId,
    id,
    apiKey,
    betaHeaders = undefined,
    compartmentId,
    liveMode,
  } = v1Context;

  const liveModeValue = getLiveMode(liveMode);
  const stripeAccountValue = accountId || null;

  if (liveModeValue !== null && !headers.has(LIVEMODE_HEADER)) {
    headers.set(LIVEMODE_HEADER, liveModeValue);
  }

  if (stripeAccountValue !== null && !headers.has(ACCOUNT_HEADER)) {
    headers.set(ACCOUNT_HEADER, stripeAccountValue);
  }

  if (id !== null && id !== undefined && !headers.has(PATHED_ACCOUNT_HEADER)) {
    headers.set(PATHED_ACCOUNT_HEADER, id);
  }

  if (compartmentId && !headers.has(STRIPE_CONTEXT_HEADER_FOR_V1)) {
    headers.set(STRIPE_CONTEXT_HEADER_FOR_V1, compartmentId);
  }

  if (apiKey && !headers.has(AUTHORIZATION_HEADER)) {
    headers.set(AUTHORIZATION_HEADER, `Bearer ${apiKey}`);
  }

  if (!headers.has(REQUESTED_WITH_HEADER)) {
    headers.set(REQUESTED_WITH_HEADER, 'XMLHttpRequest');
  }

  if (!headers.has(CONTENT_TYPE_HEADER)) {
    headers.set(CONTENT_TYPE_HEADER, 'application/x-www-form-urlencoded');
  }

  // Beta headers: https://confluence.corp.stripe.com/display/DEVX/Beta+headers.
  const betaHeadersStr = betaHeaders
    ? `;${Object.entries(betaHeaders)
        .map(([name, value]) => `${name}=${value}`)
        .join(';')}`
    : '';

  if (!headers.get(STRIPE_VERSION_HEADER)) {
    headers.set(STRIPE_VERSION_HEADER, `${version}${betaHeadersStr}`);
  }

  if (headers.get(ACCOUNT_HEADER) === '') {
    headers.delete(ACCOUNT_HEADER);
  }

  if (headers.get(STRIPE_VERSION_HEADER) === '') {
    headers.delete(STRIPE_VERSION_HEADER);
  }

  if (headers.get(CONTENT_TYPE_HEADER) === '') {
    headers.delete(CONTENT_TYPE_HEADER);
  }

  if (headers.get(LIVEMODE_HEADER) === '') {
    headers.delete(LIVEMODE_HEADER);
  }

  if (headers.get(STRIPE_CONTEXT_HEADER_FOR_V1) === '') {
    headers.delete(STRIPE_CONTEXT_HEADER_FOR_V1);
  }

  if (headers.get(REQUESTED_WITH_HEADER) === '') {
    headers.delete(REQUESTED_WITH_HEADER);
  }

  if (headers.get(AUTHORIZATION_HEADER) === '') {
    headers.delete(AUTHORIZATION_HEADER);
  }
}
